import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useClientAuthStore, useUIStore } from '../stores';
import './Client.css';

function ClientSignup() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const { signup, isLoading, error } = useClientAuthStore();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLoading) {
      return; // Prevent multiple submissions
    }

    if (!validateForm()) {
      return;
    }

    try {
      // Use Zustand client auth store for signup
      await signup({
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        password: formData.password
      });

      // Signup successful - show success notification
      useUIStore.getState().showSuccessNotification('Signup successful! Please login to continue.');
      
      // Navigate to login page
      navigate('/client/login');
      
    } catch (error) {
      // Error is already handled by the store, but we can show a notification
      useUIStore.getState().showErrorNotification(error.message || 'Signup failed. Please try again.');
    }
  };

  return (
    <div className="client-login-container">
      <div className="client-login-card">
        <h2>Client Signup</h2>
        <p className="client-login-subtitle">Create your client account</p>
        <form onSubmit={handleSubmit} className="client-login-form">
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              className={errors.name ? 'error' : ''}
              required
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter 10-digit phone number"
              className={errors.phone ? 'error' : ''}
              required
            />
            {errors.phone && <span className="field-error">{errors.phone}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="address">Address</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter your address"
              className={errors.address ? 'error' : ''}
              rows="3"
              required
            />
            {errors.address && <span className="field-error">{errors.address}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter password (min 6 characters)"
              className={errors.password ? 'error' : ''}
              required
            />
            {errors.password && <span className="field-error">{errors.password}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              className={errors.confirmPassword ? 'error' : ''}
              required
            />
            {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
          </div>
          {error && <div className="error-message">{error}</div>}
          <button 
            type="submit" 
            className="client-login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>
        <div className="client-login-footer">
          <p>Already have an account? <Link to="/client/login">Login here</Link></p>
        </div>
        {isLoading && (
          <div className="client-login-loading">
            <p>Creating your account...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientSignup;



